import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';
import { computeGranularity } from './useTimeseriesQuery';
import type { Granularity } from '@/lib/analytics/metrics';

interface RPCRow {
  period_label: 'current' | 'previous';
  bucket_date: string;
  segment: string;
  value: number;
}

export interface SegmentedBucket {
  bucket_date: string;
  segment: string;
  current: number;
  previous: number | null;
}

export interface StackedTimeSeriesResult {
  kind: 'stacked-time-series';
  buckets: SegmentedBucket[];
  segments: string[]; // stable order of all observed segments
  total_current: number;
  total_previous: number | null;
  delta_pct: number | null;
  granularity: Granularity;
  isLoading: boolean;
  error: Error | null;
}

interface UseConnectionsByTypeBreakdownArgs {
  workspaceId: string;
  from: Date;
  to: Date;
  compareFrom: Date | null;
  compareTo: Date | null;
  manualGranularity: 'auto' | Granularity;
  userFilter: string | null;
  tz: string;
  enabled?: boolean;
}

export function useConnectionsByTypeBreakdown(opts: UseConnectionsByTypeBreakdownArgs): StackedTimeSeriesResult {
  const granularity = computeGranularity(opts.from, opts.to, opts.manualGranularity);
  const hourMode = granularity === 'hour';
  const fromIso = opts.from.toISOString();
  const toIso = opts.to.toISOString();
  const effCompareFrom = hourMode ? null : opts.compareFrom;
  const effCompareTo = hourMode ? null : opts.compareTo;
  const comparisonEnabled = effCompareFrom !== null && effCompareTo !== null;
  const keyFormat = hourMode ? "yyyy-MM-dd'T'HH" : 'yyyy-MM-dd';

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.analytics.series(
      opts.workspaceId, 'connections_by_type', fromIso, toIso, granularity, opts.userFilter,
    ),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_connections_by_type_breakdown', {
        p_workspace_id: opts.workspaceId,
        p_from: fromIso,
        p_to: toIso,
        p_compare_from: effCompareFrom?.toISOString() ?? null,
        p_compare_to: effCompareTo?.toISOString() ?? null,
        p_granularity: granularity,
        p_user_filter: opts.userFilter,
        p_tz: opts.tz,
      });
      if (error) throw error;
      return (data ?? []) as RPCRow[];
    },
    enabled: opts.enabled ?? true,
    staleTime: 60_000,
  });

  const rows = (data ?? []).map((r) => ({
    ...r,
    bucket_date: format(parseISO(r.bucket_date), keyFormat),
  }));
  const segments = Array.from(new Set(rows.map((r) => r.segment))).sort();

  // Build segmented buckets keyed by (bucket_date, segment). Pivot logic happens in the chart card.
  const buckets: SegmentedBucket[] = [];
  const currentByKey = new Map<string, number>();
  const previousByKey = new Map<string, number>();
  for (const r of rows) {
    const key = `${r.bucket_date}|${r.segment}`;
    if (r.period_label === 'current') currentByKey.set(key, r.value);
    else previousByKey.set(key, r.value);
  }
  // Emit one entry per (date, segment) seen in the current window
  const currentDates = Array.from(new Set(rows.filter((r) => r.period_label === 'current').map((r) => r.bucket_date))).sort();
  for (const d of currentDates) {
    for (const seg of segments) {
      const key = `${d}|${seg}`;
      buckets.push({
        bucket_date: d,
        segment: seg,
        current: currentByKey.get(key) ?? 0,
        previous: comparisonEnabled ? (previousByKey.get(key) ?? null) : null,
      });
    }
  }

  const totalCurrent = rows.filter((r) => r.period_label === 'current').reduce((s, r) => s + r.value, 0);
  const totalPrevious = comparisonEnabled
    ? rows.filter((r) => r.period_label === 'previous').reduce((s, r) => s + r.value, 0)
    : null;

  const deltaPct =
    totalPrevious === null ? null
      : totalPrevious === 0 && totalCurrent === 0 ? 0
      : totalPrevious === 0 ? null
      : Math.round(((totalCurrent - totalPrevious) / totalPrevious) * 1000) / 10;

  return {
    kind: 'stacked-time-series',
    buckets,
    segments,
    total_current: totalCurrent,
    total_previous: totalPrevious,
    delta_pct: deltaPct,
    granularity,
    isLoading,
    error: (error as Error) ?? null,
  };
}
